import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { setBodyScroll } from 'src/utils/bodyScroll';
import styles from './DetailsUsage.module.scss';
import { FlatButton } from 'src/pages/Asset/components/FlatButton';
import { X } from 'src/common/icons';
import CheckUsageDropDown from '../CheckUsage/DropDown/DropDown';
import { Button, ChartComponent, Input } from 'src/components';
import { StatsTimeSeriesTypes } from 'src/types/TCheckUsage';
import { getTimeSeriesStats } from 'src/api/functions/statsApi';
import { useAlerts } from 'src/common/AlertManager';
import { convertSeconds, formatBillingBytes, formatNumberIntl, maxDate, minDate, validateDateRange } from 'src/utils/common';
import rollupData from 'src/utils/rollup';
import BoardingTour from 'src/components/BoardingTour';

type DetailsParams = {
  userid: string;
  user: string;
  period: string;
};

export const DetailsUsage: React.FC = () => {
  const { showAlert } = useAlerts();
  const location = useLocation<any>();
  const history = useHistory<any>();
  const hasBackground = !!location?.state?.background;
  const { data: { userData, type: stat_type, statsData, accessToken, graphTitle, graphDesc, icon } = {} as any } =
    location.state || {};

  useEffect(() => {
    if (!hasBackground) return;
    setBodyScroll('100vh', 'hidden');
    return () => setBodyScroll('', '');
  }, [hasBackground]);

  const dateRef = useRef<HTMLDivElement>(null);
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dateRef.current && !dateRef.current.contains(e.target as Node)) {
        setIsDateRangeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const back = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    const bg = history.location.state?.background;
    if (bg) history.push(bg.pathname);
    else history.goBack();
  };

  const getTodayString = () => new Date().toISOString().slice(0, 10);
  const getWeekAgoString = () => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  };

  const [startDate, setStartDate] = useState(() => getWeekAgoString());
  const [endDate, setEndDate] = useState(() => getTodayString());
  
  const [tempStartDate, setTempStartDate] = useState(() => getWeekAgoString());
  const [tempEndDate, setTempEndDate] = useState(() => getTodayString());

  let { user, userid, period } = useParams<DetailsParams>();
  const periodMap: Record<string, string> = {
    '168': 'Past 1 Week',
    '730': 'Past 1 Month',
    '8760': 'Past 1 Year',
  };
  const queryUser = user ?? 'all';
  const queryUserId = userid ? parseInt(userid) : null;
  // The usage page links here with the label itself ("Past 1 Year"), older
  // links with the hour count; accept both.
  const queryTimeRange =
    periodMap[period] ?? (Object.values(periodMap).includes(period) ? period : 'Past 1 Week');

  const [selectedUser, setSelectedUser] = useState<string>(queryUser);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(queryUserId);
  const [selectedApiSubType, setSelectedApiSubType] = useState<string>(
    stat_type === 'cdn' ? 'Traffic' : stat_type === 'api' ? 'All API' : stat_type === 'ai' ? statsData?.[0]?.key ?? '' : '',
  );
  const [selectedTimeRange, setSelectedTimeRange] = useState<string>(queryTimeRange);
  const [timeRange, setTimeRange] = useState<string>(queryTimeRange);

  const [apiResponse, setApiResponse] = useState<StatsTimeSeriesTypes>();
  const [graphData, setGraphData] = useState<{ timestamp: number; value: number | string }[]>([]);

  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const userOptions = useMemo(() => {
    const allOption = {
      key: 'all',
      value: (
        <div className={styles.optionRow}>
          <span className={styles.userName}>All Users</span>
        </div>
      ),
    };

    const users =
      userData?.map((u: any) => ({
        key: String(u.id),
        value: (
          <div className={styles.optionRow}>
            <span className={styles.userName}>{u.first_name}</span>
            <span className={styles.userEmail}>{u.email}</span>
          </div>
        ),
      })) || [];

    return [allOption, ...users];
  }, [userData]);

  const apiOptions = useMemo(
    () => statsData?.map((el: any) => ({ key: el.key, value: <span>{el?.value ? el?.value : el.key}</span> })) || [],
    [statsData],
  );

  const timeOptions = ['Past 1 Week', 'Past 1 Month', 'Past 1 Year', 'Custom'].map((key) => ({
    key,
    value: <span>{key}</span>,
  }));

  const totalValue = useMemo(() => {
    if (!apiResponse || !apiResponse.data) return 0;
    const dynamicKeys = Object.keys(apiResponse.data);
    if (dynamicKeys.length === 0) return 0;
    const firstKey = dynamicKeys[0];
    const routeMap = apiResponse.data[firstKey];
    if (routeMap.length === 0) return 0;
    const total = routeMap.reduce((acc, dp) => acc + dp.value, 0);
    
    if (stat_type === 'storage') {
      // For storage, sort by timestamp and get the most recent value
      // Storage is cumulative, so the latest timestamp has the current storage value
      const sortedData = [...routeMap].sort((a, b) => a.timestamp - b.timestamp);
      const latestValue = sortedData[sortedData.length - 1]?.value || 0;
      return formatBillingBytes(latestValue);
    }
    
    return stat_type === 'cdn' && selectedApiSubType === 'Traffic'
      ? formatBillingBytes(total)
      : stat_type === 'transcodes'
      ? convertSeconds(total)
      : stat_type === 'lb' ? formatBillingBytes(total):formatNumberIntl(total);
  }, [apiResponse, stat_type, selectedApiSubType]);

  const handleDatePickerApply = () => {
    if (!tempStartDate || !tempEndDate) {
      showAlert('Please select both start and end dates');
      return;
    }

    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setIsDateRangeOpen(false);
    
    fetchStatsWithDates(tempStartDate, tempEndDate);
  };

  const handleDatePickerCancel = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setIsDateRangeOpen(false);
  };

  const fetchStatsWithDates = useCallback(async (customStartDate?: string, customEndDate?: string) => {
    setIsFetching(true);
    setHasFetched(false);

    const dateStart = customStartDate || startDate;
    const dateEnd = customEndDate || endDate;

    if (!dateStart || !dateEnd) {
      const weekAgo = getWeekAgoString();
      const today = getTodayString();
      
      try {
        const statSubtype =
          stat_type === 'cdn'
            ? selectedApiSubType?.toLowerCase()
            : selectedApiSubType === 'All API'
            ? ''
            : selectedApiSubType;

        const res: any = await getTimeSeriesStats(
          accessToken,
          weekAgo,
          today,
          selectedUserId,
          stat_type,
          statSubtype,
          'Past 1 Week',
        );

        if (!res || !res.data) throw new Error();

        const keys = Object.keys(res.data);
        if (keys.length === 0) {
          setApiResponse(res as StatsTimeSeriesTypes);
          return;
        }

        const firstKey = keys[0];
        const dataList = res.data[firstKey];

        const processed = dataList.length > 0 ? rollupData(res, 13, stat_type) : res;
        setApiResponse(processed as StatsTimeSeriesTypes);
      } catch {
        showAlert('Error fetching data');
        setApiResponse(undefined);
      } finally {
        setIsFetching(false);
        setHasFetched(true);
      }
      return;
    }
    try {
      const statSubtype =
        stat_type === 'cdn'
          ? selectedApiSubType?.toLowerCase()
          : selectedApiSubType === 'All API'
          ? ''
          : selectedApiSubType;

      const res: any = await getTimeSeriesStats(
        accessToken,
        dateStart,
        dateEnd,
        selectedUserId,
        stat_type,
        statSubtype,
        timeRange,
      );

      if (!res || !res.data) throw new Error();

      const keys = Object.keys(res.data);
      if (keys.length === 0) {
        setApiResponse(res as StatsTimeSeriesTypes);
        return;
      }

      const firstKey = keys[0];
      const dataList = res.data[firstKey];

      const processed = dataList.length > 0 ? rollupData(res, 13, stat_type) : res;
      setApiResponse(processed as StatsTimeSeriesTypes);
    } catch {
      showAlert('Error fetching data');
      setApiResponse(undefined);
    } finally {
      setIsFetching(false);
      setHasFetched(true);
    }
  }, [accessToken, stat_type, startDate, endDate, selectedUserId, selectedApiSubType, timeRange]);

  const fetchStats = useCallback(async () => {
    return fetchStatsWithDates();
  }, [fetchStatsWithDates]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (!apiResponse || !apiResponse.data) {
      setGraphData([]);
      return;
    }

    const keys = Object.keys(apiResponse.data);
    if (keys.length === 0) {
      setGraphData([]);
      return;
    }

    const firstKey = keys[0];
    const rawData = apiResponse.data[firstKey];

    if (rawData.length === 0) {
      setGraphData([]);
      return;
    }

    // Sorting the data by timestamp
    const sortedData = [...rawData].sort((a, b) => a.timestamp - b.timestamp);

    const formatted = sortedData.map((dp) => ({
      timestamp: dp.timestamp,
      value: stat_type === 'cdn' && selectedApiSubType === 'Traffic' ? formatBillingBytes(dp.value) : stat_type === 'lb' || stat_type === 'storage' ? formatBillingBytes(dp.value): dp.value,
    }));

    setGraphData(formatted);
  }, [apiResponse, stat_type, selectedApiSubType]);

  return (
    <><BoardingTour title='Details Usage' description='Get a grip on details usage stats for different properties and have its granulartiy' path='/usage/details/:userid/:user/:period' uniqueId='DETAILS_USAGE' /><div className={hasBackground ? styles.modalOverlay : ''} onClick={back}>
      <div className={hasBackground ? styles.modalContent : styles.container} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>{hasBackground && <FlatButton onClick={back} icon={<X />} />}</div>
        <div className={styles.intro}>
          <div className={styles.textBlock}>
            <div className={styles.titleContainer}>
              <h2 className={styles.title}>{graphTitle}</h2>
              <span className={styles.totalValue}>
                {isFetching ? <span className={styles.shimmerWrapper}>&nbsp;</span> : <span>({totalValue})</span>}
              </span>
            </div>

            <p className={styles.description}>
              {timeRange === ''
                ? `${new Date(startDate).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })} - ${new Date(endDate).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}`
                : timeRange}
            </p>
          </div>
          <img className={styles.icon} src={icon} alt="icon" />
        </div>
        <div className={styles.controls}>
          <CheckUsageDropDown
            options={userOptions}
            selectedKeys={[String(selectedUser)]}
            onClick={(k) => {
              setSelectedUser(k);
              setSelectedUserId(k === 'all' ? null : parseInt(k));
            }}
            placeholder="Select User"
            menuRightOffset={0} />
          <div className={styles.controlsRight}>
            {stat_type !== 'transcodes' && stat_type !== 'lb'  && stat_type !== 'storage' && (
              <CheckUsageDropDown
                options={apiOptions}
                selectedKeys={[selectedApiSubType]}
                onClick={setSelectedApiSubType}
                placeholder={`Select ${stat_type?.toUpperCase()} Type`}
                isSearchable={stat_type !== 'cdn'}
              />
            )}
            <CheckUsageDropDown
              options={timeOptions}
              selectedKeys={[selectedTimeRange]}
              onClick={(k) => {
                setSelectedTimeRange(k);
                setTimeRange(k === 'Custom' ? '' : k);
                if (k === 'Custom') {
                  setTempStartDate(startDate);
                  setTempEndDate(endDate);
                  setIsDateRangeOpen(true);
                } else {
                  setIsDateRangeOpen(false);
                }
              }}
              placeholder="Select Period"
              isSearchable={false} // This will hide the search input
            />
          </div>
          {isDateRangeOpen && (
            <div className={styles.datePicker} ref={dateRef}>
              <div className={styles.datePickerHeader}>Select Date Range</div>
              <Input
                type="date"
                label="Start Date"
                value={tempStartDate}
                onChange={(v) => setTempStartDate(v)}
                min={minDate}
                max={maxDate} />
              <Input
                type="date"
                label="End Date"
                value={tempEndDate}
                onChange={(v) => setTempEndDate(v)}
                min={minDate}
                max={maxDate}
              />
              <div className={styles.datePickerActions}>
                <Button 
                  className={styles.cancelButton} 
                  size='small'
                  onClick={handleDatePickerCancel}
                  disabled={!startDate || !endDate}
                >
                  Cancel
                </Button>
                <Button 
                size='small'
                  className={styles.applyButton} 
                  onClick={handleDatePickerApply}
                >
                  Apply
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className={styles.chartWrapper}>
          <ChartComponent
            statsData={graphData}
            isLoading={isFetching}
            hasFetched={hasFetched}
            graphTitle=""
            valueInBytes={stat_type === 'lb' || (stat_type === 'cdn' && selectedApiSubType === 'Traffic') || stat_type === 'storage'}
            valuesInMinutes={stat_type === 'transcodes'}
            utcDates
            graphType="line"
            title=""
            subtitle=""
            suffixXAxis={true}
            gridStyle={true}
            stat_type={stat_type}
            stat_subtype={selectedApiSubType}
          />
        </div>
      </div>
    </div></>
  );
};